import { useRef, useCallback, useEffect, useState } from 'react';

import { __DEV__ } from '@toolbox/utils';
import { useMutRef } from './useMutRef';

import type { Dispatch, SetStateAction } from 'react';

export type UseUncontrolledPropValue<T> = [T, (value: SetStateAction<T>) => void];

export function useUncontrolledProp<T>(
  valueProp?: T,
  defaultValue?: T | (() => T),
  handler?: (newValue: T) => void,
): [T, Dispatch<SetStateAction<T>>] {
  const [valueState, setValueState] = useState(defaultValue);
  const { current: isControlled } = useRef(valueProp !== undefined);
  const value = isControlled ? valueProp : valueState;
  const handlerRef = useMutRef(handler);
  const valueRef = useMutRef(value);

  if (__DEV__) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      if (isControlled !== (valueProp !== undefined)) {
        // eslint-disable-next-line no-console
        console.error(
          [
            `A component is changing the ${isControlled ? '' : 'un'}controlled value state to be ${
              isControlled ? 'un' : ''
            }controlled.`,
            'Elements should not switch from uncontrolled to controlled (or vice versa).',
            'Decide between using a controlled or uncontrolled value element for the lifetime of the component.',
            "The nature of the state is determined during the first render. It's considered controlled if the value is not `undefined`.",
            'More info: https://fb.me/react-controlled-components',
          ].join('\n'),
        );
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [valueProp]);
  }

  const setValue = useCallback((newValueFn: SetStateAction<T>) => {
    const newValue = isFunction(newValueFn) ? newValueFn(valueRef.current as T) : newValueFn;
    if (!isControlled) setValueState(newValue);
    handlerRef.current?.(newValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [value as T, setValue];
}

// eslint-disable-next-line @typescript-eslint/ban-types
function isFunction(v: unknown): v is Function {
  return typeof v === 'function';
}
